import React, { useEffect, useState } from 'react';
import {
  Card, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import { getPendingSignups, approveSignup, rejectSignup } from 'services/pilotApprovalService';

function PilotApprovals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // token currently being acted on
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirm, setConfirm] = useState({ open: false, row: null, action: null });

  const load = async () => {
    try {
      setLoading(true);
      const data = await getPendingSignups();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading pending signups:', err);
      setSnackbar({ open: true, message: 'Failed to load pending signups', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runAction = async () => {
    const { row, action } = confirm;
    setConfirm({ open: false, row: null, action: null });
    setBusy(row.pk);
    try {
      if (action === 'approve') {
        await approveSignup(row.pk);
        setSnackbar({ open: true, message: 'Approved and provisioned', severity: 'success' });
      } else {
        await rejectSignup(row.pk);
        setSnackbar({ open: true, message: 'Signup rejected', severity: 'success' });
      }
      await load();
    } catch (err) {
      console.error('Action failed:', err);
      setSnackbar({ open: true, message: 'Action failed', severity: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h6">Pending Approvals</SoftTypography>
            <SoftTypography variant="body2" color="text">
              Dealer signups that verified their email but need review before provisioning.
            </SoftTypography>
          </SoftBox>
          {loading ? (
            <SoftBox p={3}><CircularProgress /></SoftBox>
          ) : rows.length === 0 ? (
            <SoftBox p={3}><Alert severity="info">No signups awaiting approval.</Alert></SoftBox>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ display: 'table-header-group' }}>
                  <TableRow>
                    <TableCell>Dealership</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Rep</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.pk}>
                      <TableCell>{r.dealershipName}</TableCell>
                      <TableCell>{r.contactName}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>{r.phone || '—'}</TableCell>
                      <TableCell>{r.salesRep || (r.leadId ? 'Lead' : '—')}</TableCell>
                      <TableCell align="right">
                        {busy === r.pk ? (
                          <CircularProgress size={18} />
                        ) : (
                          <>
                            <SoftButton size="small" variant="text" color="success"
                              onClick={() => setConfirm({ open: true, row: r, action: 'approve' })}>
                              Approve
                            </SoftButton>
                            <SoftButton size="small" variant="text" color="error"
                              onClick={() => setConfirm({ open: true, row: r, action: 'reject' })}>
                              Reject
                            </SoftButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </SoftBox>

      <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, row: null, action: null })}>
        <DialogTitle>{confirm.action === 'approve' ? 'Approve & provision' : 'Reject signup'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm.action === 'approve'
              ? `Provision a trial for ${confirm.row?.dealershipName}? This creates the tenant and emails login details.`
              : `Reject the signup for ${confirm.row?.dealershipName}? No tenant will be created.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, row: null, action: null })}>Cancel</Button>
          <Button onClick={runAction} color={confirm.action === 'approve' ? 'success' : 'error'} variant="contained">
            {confirm.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default PilotApprovals;
